import { yupResolver } from '@hookform/resolvers/yup'
import { useCallback, useEffect } from 'react'
import { DeepPartial, DefaultValues, FieldValues, Resolver, UseFormReturn, useForm } from 'react-hook-form'
import * as yup from 'yup'

type CustomFormHook<T extends FieldValues> = {
  control: UseFormReturn<T>['control']
  formState: UseFormReturn<T>['formState']
  handleSubmit: UseFormReturn<T>['handleSubmit']
  reset: () => void
}

export function useCustomFormWithReset<T extends FieldValues, U>(
  data: U | undefined | null,
  getDefaultValues: (data: U | undefined | null) => DeepPartial<T>,
  schema: yup.ObjectSchema<yup.AnyObject>
): CustomFormHook<T> {
  const { control, formState, handleSubmit, reset } = useForm<T>({
    defaultValues: getDefaultValues(data) as DefaultValues<T>,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    // Double cast required: yupResolver returns Resolver<MakeKeysOptional<AnyObject>>
    // which is structurally incompatible with the generic Resolver<T>. This is a known
    // limitation of @hookform/resolvers when used in generic hooks — the runtime behavior
    // is correct, only the types don't bridge.
    resolver: yupResolver(schema) as unknown as Resolver<T>,
  })

  const resetForm = useCallback(() => {
    return reset(getDefaultValues(data) as T)
  }, [data, getDefaultValues, reset])

  useEffect(() => {
    resetForm()
  }, [data, resetForm])

  return {
    control,
    formState,
    handleSubmit,
    reset: resetForm,
  }
}
